/**
 * One button system, for both halves of the site.
 *
 * There were nineteen distinct button designs: ten hand-rolled Tailwind
 * signatures across the React pages, and nine CSS rules inside the guide
 * documents. They disagreed about every property a button has.
 *
 * The sharpest tell was the corner. The guide documents use `border-radius:
 * 2px` and `3px` for forty of their sixty radius declarations, so their
 * buttons and cards are effectively square, while the rest of the site is
 * built on `--radius: 0.5rem`. That is why a guide reads as a different site
 * even after the palette was fixed: the shapes are wrong, not just the colours.
 *
 * Height disagreed too. shadcn's Button primitive defaults to h-9, which is
 * 36px, below the 44px minimum the rest of the site uses. That primitive is
 * present in the repo but no page uses it, which is how fourteen files ended
 * up each inventing their own.
 *
 * So there are two representations of the same decisions here:
 *
 *   BUTTON      Tailwind class strings, for the React pages.
 *   BUTTON_CSS  the same rules as plain CSS, for the eight guide documents,
 *               which live inside an iframe and have no Tailwind.
 *
 * They are written next to each other so a change to one is an obvious
 * omission if it is not made to the other, and buttonStyles.test.ts checks
 * the metrics agree.
 */

/** 44px. The floor for anything tappable; the audience skews 55+. */
const COMPACT_HEIGHT = "min-h-11";
/** 48px. Primary calls to action. */
const PRIMARY_HEIGHT = "min-h-12";

/** Shared by every variant: layout, radius, weight, transition. */
const BASE =
  "inline-flex items-center justify-center gap-2 rounded-md font-semibold " +
  "transition-opacity disabled:opacity-60 disabled:pointer-events-none " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2";

export const BUTTON = {
  /** The one action you want them to take on a page. */
  primary: `${BASE} ${PRIMARY_HEIGHT} px-5 text-base bg-primary text-primary-foreground hover:opacity-90`,
  /** Same shape, less weight. For the second action beside a primary. */
  secondary: `${BASE} ${PRIMARY_HEIGHT} px-5 text-base bg-card text-foreground border border-border hover:bg-muted`,
  /** Compact primary: toolbars, cards, anywhere a 48px button would dominate. */
  primarySm: `${BASE} ${COMPACT_HEIGHT} px-4 text-sm bg-primary text-primary-foreground hover:opacity-90`,
  /** Compact secondary. The most common button on a tool path. */
  secondarySm: `${BASE} ${COMPACT_HEIGHT} px-4 text-sm bg-card text-foreground border border-border hover:bg-muted`,
  /** No fill, no border. Tertiary actions like "Start over". */
  ghost: `${BASE} ${COMPACT_HEIGHT} px-3 text-sm text-foreground/70 hover:text-foreground hover:bg-muted`,
} as const;

export type ButtonVariant = keyof typeof BUTTON;

/**
 * The same buttons as CSS, injected into the guide documents.
 *
 * Targets the class names those documents already use rather than asking them
 * to adopt new markup: .mark-done-btn, .copy-btn, .action-btn, .tab-btn,
 * .back-btn, .copy-all-btn and .mark-week-btn are the seven that exist.
 *
 * `--yss-*` values are set by GUIDE_CSS_VARS in guideTheme.
 */
export const BUTTON_CSS = `
  .mark-done-btn,
  .copy-btn,
  .copy-all-btn,
  .action-btn,
  .mark-week-btn,
  .tab-btn,
  .filter-pill,
  .other-pill,
  .back-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 44px;
    padding: 0 16px;
    border-radius: 6px;
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
    transition: opacity 0.2s ease, background-color 0.2s ease;
  }

  .mark-done-btn,
  .action-btn,
  .mark-week-btn {
    background: var(--terracotta);
    color: var(--cream);
    border: 1px solid var(--terracotta);
  }

  .copy-btn,
  .copy-all-btn,
  .tab-btn,
  .filter-pill,
  .other-pill {
    background: var(--white);
    color: var(--espresso);
    border: 1px solid var(--border);
  }

  /* A text back-link rather than a filled control, but it still needs the
     44px target: it was 12px type with no padding at all. */
  .back-btn {
    background: transparent;
    border: 0;
    color: var(--body);
    padding: 0 8px;
  }

  .mark-done-btn:hover,
  .action-btn:hover,
  .mark-week-btn:hover { opacity: 0.9; }

  .copy-btn:hover,
  .copy-all-btn:hover,
  .tab-btn:hover,
  .filter-pill:hover,
  .other-pill:hover { background: var(--linen); }

  .back-btn:hover { color: var(--espresso); background: var(--linen); }

  .filter-pill.active,
  .other-pill.active {
    background: var(--terracotta);
    border-color: var(--terracotta);
    color: var(--cream);
  }

  /* Whatever "done" or "active" state each document uses. */
  .mark-done-btn.done,
  .mark-week-btn.done,
  .tab-btn.active {
    background: var(--sage);
    border-color: var(--sage);
    color: var(--cream);
  }
`;

/**
 * Rounds the guide documents' corners to match the rest of the site.
 *
 * Set on the element rather than per-rule because the radii are spread across
 * sixty declarations in eight files. 2px and 3px both become 6px; anything the
 * documents deliberately made round (50% avatars, pills) is left alone by
 * only targeting the containers that carry the small values.
 */
export const RADIUS_CSS = `
  /* Containers. 8px, the site's --radius. */
  .step,
  .tip-box,
  .completion,
  .scenario-card,
  .win-card,
  .hook-card,
  .practice-card,
  .week,
  .flywheel,
  .flywheel-step,
  .toast,
  .search-input {
    border-radius: 8px;
  }

  /* Controls and pills, which want the tighter 6px. */
  .completion a,
  .other-pill,
  .filter-pill,
  .day-checkbox {
    border-radius: 6px;
  }

  /* Progress bars stay fully round: a 2px radius on a 6px-tall bar reads as
     a rectangle, and these are the one place the documents were reaching for
     a pill rather than a box. */
  .progress-bar,
  .progress-fill {
    border-radius: 999px;
  }
`;
